import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import EditRecurrence from './EditRecurrence';
import '../styles/TransactionPage.css';
import '../styles/ManageRecurrences.css';
import useIsMobile from '../hooks/useIsMobile';
import Icon from '@mdi/react';
import { mdiContentDuplicate, mdiDeleteOutline, mdiSquareEditOutline, mdiPlus } from '@mdi/js';

const frequencyLabels = {
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly'
};


const ManageRecurrences = () => {
    const [recurrences, setRecurrences] = useState([]);
    const [showCreatePanel, setShowCreatePanel] = useState(false);
    const [editingRecurrence, setEditingRecurrence] = useState(null);
    const [editPanelOpen, setEditPanelOpen] = useState(false);
    const [duplicateRecurrence, setDuplicateRecurrence] = useState(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        fetchRecurrences();
    }, []);

    const fetchRecurrences = async () => {
        try {
            const res = await api.get('/recurrences');
            setRecurrences(res.data);
        } catch (error) {
            console.error('Error fetching recurrences:', error);
        }
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this recurrence?');
        if (confirmDelete) {
            try {
                await api.delete(`/recurrences/${id}`);
                fetchRecurrences();
            } catch (error) {
                console.error('Error deleting recurrence:', error);
                window.alert(
                    'Failed to delete recurrence: ' +
                    (error.response?.data?.error || error.message)
                );
            }
        }
    };

    const handleEditClick = (recurrence) => {
        setEditingRecurrence(recurrence);
        setEditPanelOpen(true);
    };

    const handleDuplicateClick = (recurrence) => {
        setDuplicateRecurrence({
            ...recurrence,
            id: undefined,
            name: `${recurrence.name} (Copy)`
        });
        setEditPanelOpen(true);
    };

    const handleSuccess = () => {
        setEditPanelOpen(false);
        setShowCreatePanel(false);
        fetchRecurrences();
    };

    return (
        <div className="card">
            <div className="transaction-table recurrences-table">
                <h2 className="page-title">Manage Recurring Transactions</h2>

                <div className="header">
                    <div className="buttons-bar">
                        <button
                            className="btn btn--primary"
                            onClick={() => setShowCreatePanel(true)}
                            type="button"
                            data-tour="create-recurrence"
                        >
                            <Icon path={mdiPlus} size={0.8} /> Create Recurrence
                        </button>
                    </div>
                </div>

                <div className="table-wrapper">
                    {isMobile ? (
                        <ul className="recurrence-list">
                            {recurrences.length === 0 && (
                                <li className="empty-row">No recurrences yet. Click "Create Recurrence" to add one.</li>
                            )}
                            {recurrences.map((recurrence) => (
                                <li key={recurrence.id} className="recurrence-item">
                                    <div className="recurrence-summary">
                                        <span className="recurrence-name">{recurrence.subject_name || recurrence.name || '—'}</span>
                                                                                <span className="recurrence-amount">{recurrence.type === 'Expense' ? '-' : ''}{Math.abs(recurrence.amount)}</span>
                                    </div>
                                    <div className="recurrence-details">
                                        <p><strong>Frequency:</strong> {frequencyLabels[recurrence.frequency]} every {recurrence.interval}</p>
                                        <p><strong>Next run:</strong> {recurrence.next_run_date}</p>
                                        {recurrence.end_date && <p><strong>Ends:</strong> {recurrence.end_date}</p>}
                                        <p><strong>Account:</strong> {recurrence.account_name || '—'}</p>
                                        <p><strong>Category:</strong> {recurrence.category_name || '—'}</p>
                                        {recurrence.notes && <p><strong>Notes:</strong> {recurrence.notes}</p>}
                                    </div>
                                    <div className="mobile-actions">
                                        <button
                                            className="btn btn--ghost"
                                            onClick={() => handleEditClick(recurrence)}
                                            title="Edit"
                                        >
                                            <Icon path={mdiSquareEditOutline} size={0.75} />
                                        </button>
                                        <button
                                            className="btn btn--ghost"
                                            onClick={() => handleDuplicateClick(recurrence)}
                                            title="Duplicate"
                                        >
                                            <Icon path={mdiContentDuplicate} size={0.75} />
                                        </button>
                                        <button
                                            className="btn btn--ghost"
                                            onClick={() => handleDelete(recurrence.id)}
                                            title="Delete"
                                        >
                                            <Icon path={mdiDeleteOutline} size={0.75} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Frequency</th>
                                <th>Interval</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Next Run</th>
                                <th>Amount</th>
                                <th>Account</th>
                                <th>Subject</th>
                                <th>Category</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recurrences.map((recurrence) => (
                                <tr key={recurrence.id}>
                                    <td>{frequencyLabels[recurrence.frequency]}</td>
                                    <td>{recurrence.interval}</td>
                                    <td>{recurrence.start_date}</td>
                                    <td>{recurrence.end_date || 'N/A'}</td>
                                    <td>{recurrence.next_run_date}</td>
                                    <td>{recurrence.type === 'Expense' ? '-' : ''}{Math.abs(recurrence.amount)}</td>
                                    <td>{recurrence.account_name}</td>
                                    <td>{recurrence.subject_name}</td>
                                    <td>{recurrence.category_name}</td>
                                    <td>
                                        <button
                                            className="btn btn--ghost"
                                            onClick={() => handleEditClick(recurrence)}
                                        >
                                            <Icon path={mdiSquareEditOutline} size={0.75} />
                                        </button>
                                        <button
                                            className="btn btn--ghost"
                                            onClick={() => handleDuplicateClick(recurrence)}
                                        >
                                            <Icon path={mdiContentDuplicate} size={0.75} />
                                        </button>
                                        <button
                                            className="btn btn--ghost"
                                            onClick={() => handleDelete(recurrence.id)}
                                        >
                                            <Icon path={mdiDeleteOutline} size={0.75} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    )}
                </div>

                {(showCreatePanel || editPanelOpen) && (
                    <div className="edit-panel create-panel">
                        <div className="panel-header">
                            <h3>{editingRecurrence ? 'Edit' : duplicateRecurrence ? 'Duplicate' : 'New'} Recurrence</h3>
                            <button onClick={() => {
                                setEditPanelOpen(false);
                                setShowCreatePanel(false);
                                setEditingRecurrence(null);
                                setDuplicateRecurrence(null);
                            }}>×</button>
                        </div>
                        <EditRecurrence
                            recurrence={editingRecurrence || duplicateRecurrence}
                            onCancel={() => {
                                setEditPanelOpen(false);
                                setShowCreatePanel(false);
                                setEditingRecurrence(null);
                                setDuplicateRecurrence(null);
                            }}
                            onSuccess={handleSuccess}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageRecurrences;